'use client';

import { useCallback, useRef, useState } from 'react';
import { initialSteps } from './runner';
import { streamAgentRun } from './stream-client';
import type { AgentRunState, AgentStepEvent } from './types';

/**
 * Agent kit — client hook for any streaming agent endpoint.
 *
 * Steps start as the full PENDING plan rather than an empty list, so the UI can
 * render every row immediately and light them up as they land.
 */

export function idleAgentState<TKey extends string, TResult>(
  keys: readonly TKey[],
): AgentRunState<TKey, TResult> {
  return {
    status: 'idle',
    steps: initialSteps(keys),
    currentStep: null,
    result: null,
    trace: null,
    error: null,
  };
}

/** Fold one stream event into the view state. Pure, so it is directly testable. */
export function applyAgentEvent<TKey extends string, TResult>(
  state: AgentRunState<TKey, TResult>,
  event: AgentStepEvent<TKey, TResult>,
): AgentRunState<TKey, TResult> {
  if (event.type === 'step') {
    return {
      ...state,
      currentStep:
        event.step.status === 'RUNNING' ? event.step.step_key : state.currentStep,
      steps: state.steps.map((step) =>
        step.step_key === event.step.step_key ? event.step : step,
      ),
    };
  }
  if (event.type === 'done') {
    // Every flow's result carries its own trace; the kit only needs that field.
    const trace =
      (event.result as { trace?: AgentRunState<TKey, TResult>['trace'] } | null)?.trace ?? null;
    return {
      ...state,
      status: 'ready',
      currentStep: null,
      result: event.result as TResult,
      trace,
      // The trace is authoritative over whatever we accumulated en route.
      steps: trace?.steps ?? state.steps,
    };
  }
  return { ...state, status: 'error', error: event.message };
}

export function useAgentRun<TKey extends string, TResult>(
  endpoint: string,
  keys: readonly TKey[],
) {
  const [state, setState] = useState<AgentRunState<TKey, TResult>>(() =>
    idleAgentState<TKey, TResult>(keys),
  );
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(idleAgentState<TKey, TResult>(keys));
  }, [keys]);

  const run = useCallback(
    async (payload: Record<string, unknown>) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState({ ...idleAgentState<TKey, TResult>(keys), status: 'running' });

      try {
        return await streamAgentRun<TKey, TResult>(endpoint, payload, {
          signal: controller.signal,
          onEvent: (event) => setState((prev) => applyAgentEvent(prev, event)),
        });
      } catch (error) {
        if (controller.signal.aborted) return null;
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: error instanceof Error ? error.message : 'Request failed',
        }));
        return null;
      }
    },
    [endpoint, keys],
  );

  return { ...state, run, reset };
}
